import { isString, reduce } from 'lodash/fp';
import { FieldProps, FieldRenderProps } from 'react-final-form';

export const REGEXES = {
    email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    number: /^[0-9]+$/,
    urlDomain: /^[A-Za-z0-9_-]+$/,
    urlProtocol: /^(http|https):\/\//,
    publicKey: /(ssh-\S+\s)(.*?)(\s.*)/,
    id: /^[a-zA-Z0-9*-]+$/,
};

type ValidationContext = { label?: string };
type ValidateFunction = (
    value: FieldProps<string, FieldRenderProps<string, HTMLElement>>['value'],
    context: ValidationContext
) => string | null;

export type Validator = {
    inputProps?: Partial<HTMLInputElement>;
    validate: ValidateFunction;
};

export const getValidateFunction =
    (validators?: Validator[]) =>
    (value: string, context: ValidationContext): string | null => {
        if (validators) {
            return (
                validators.reduce<string | null>(
                    (error, validator) => error || validator.validate(value, context),
                    null
                ) || null
            );
        } else {
            return null;
        }
    };

type InputProps = Partial<HTMLInputElement>;

export const getInputProps = (validators: Validator[]): InputProps => {
    return reduce(
        (result, validator) => ({ ...result, ...validator.inputProps }),
        { required: false } as InputProps,
        validators
    );
};

export const VALIDATORS = {
    required: (params: { errorMessage?: string } = {}): Validator => ({
        inputProps: { required: true },
        validate: (
            value: FieldProps<string, FieldRenderProps<string, HTMLElement>>['value'],
            { label }: ValidationContext
        ) => {
            const isInvalid = !value || (isString(value) && !value.length);
            return isInvalid ? params.errorMessage || getRequiredErrorMessage(label) : null;
        },
    }),
    email: () => ({
        validate: (value: FieldProps<string, FieldRenderProps<string, any>>['value']) =>
            value && REGEXES.email.test(value) ? null : getEmailErrorMessage(),
    }),
    number: (params: { min?: number; max?: number } = {}) => ({
        validate: (value: FieldProps<string, FieldRenderProps<string, HTMLElement>>['value']) => {
            if (!REGEXES.number.test(String(value))) {
                return getNumberErrorMessage();
            }
            const number = parseInt(String(value));
            if (
                (params.min !== undefined && number < params.min) ||
                (params.max !== undefined && number > params.max)
            ) {
                return getNumberRangeErrorMessage(params.min, params.max);
            }
            return null;
        },
    }),
    urlDomain: () => ({
        validate: (value: FieldProps<string, FieldRenderProps<string, HTMLElement>>['value']) =>
            REGEXES.urlDomain.test(String(value)) ? null : getUrlDomainErrorMessage(),
    }),
    urlProtocol: () => ({
        validate: (value: FieldProps<string, FieldRenderProps<string, HTMLElement>>['value']) =>
            REGEXES.urlProtocol.test(String(value)) ? null : getUrlProtocolErrorMessage(),
    }),
    length: (params: { minLength?: number; maxLength?: number } = {}) => ({
        inputProps: { minLength: params.minLength, maxLength: params.maxLength },
        validate: (value: FieldProps<any, FieldRenderProps<any, any>>['value']) => {
            const isTooShort = params.minLength && (!value || value.length < params.minLength);
            const isTooLong = params.maxLength && value && value.length > params.maxLength;
            return isTooShort || isTooLong ? getLengthErrorMessage(params.minLength, params.maxLength) : null;
        },
    }),
    valueAlreadyExists: (params: { existingValues: string[]; errorMessage: string }) => ({
        validate: (value: FieldProps<string, FieldRenderProps<string, any>>['value']) =>
            value && params.existingValues.includes(value) ? params.errorMessage : null,
    }),
    exactLineLength: (requiredLength: number) => ({
        validate: (
            value: FieldProps<string, FieldRenderProps<string, HTMLElement>>['value'],
            { label }: ValidationContext
        ) => {
            const falseyIds = value?.split('\n').filter(row => {
                return row.length !== requiredLength && row.length !== 0;
            });

            return falseyIds && falseyIds.length > 0
                ? `Each ${label} must have a length of ${requiredLength}. First invalid line: "${falseyIds[0]}"`
                : null;
        },
    }),
    publicKey: () => ({
        validate: (value: FieldProps<string, FieldRenderProps<string, any>>['value']) =>
            value && REGEXES.publicKey.test(value) ? null : 'Please enter valid public key',
    }),
} satisfies Record<string, (params?: any) => Validator>;

export function getRequiredErrorMessage(label?: string) {
    label = label || 'value';
    const article = getArticle(label);
    return `Please enter ${article} ${label}.`;
}

export function getNumberErrorMessage() {
    return `Please only use numbers.`;
}

export function getNumberRangeErrorMessage(min?: number, max?: number) {
    if (min !== undefined && max !== undefined) {
        return `Please only use numbers between ${min} and ${max}.`;
    }
    if (min !== undefined) {
        return `Please only use numbers greater than or equal to ${min}.`;
    }
    if (max !== undefined) {
        return `Please only use numbers lower than or equal to ${max}.`;
    }
    return null;
}

export function getUrlDomainErrorMessage() {
    return `Please only use alphanumeric characters, underscore or dash.`;
}

export function getUrlProtocolErrorMessage() {
    return `Please enter a valid url that starts with https:// or http://`;
}

export function getEmailErrorMessage() {
    return `Please enter a valid email address.`;
}

export function getLengthErrorMessage(minLength?: number, maxLength?: number) {
    if (minLength && !maxLength) {
        return `Please enter at least ${minLength} characters.`;
    }
    if (!minLength && maxLength) {
        return `Please enter less than ${maxLength} characters.`;
    }
    return `Please enter between ${minLength} and ${maxLength} characters.`;
}

export function getArticle(label: string) {
    const firstLetter = label[0].toLowerCase();
    const startsWithVowel = ['a', 'e', 'i', 'o', 'u'].includes(firstLetter);
    return startsWithVowel ? 'an' : 'a';
}
